if (!customElements.get('product-gallery')) {
  class ProductGallery extends HTMLElement {
    async connectedCallback() {
      this.main = this.querySelector('[data-gallery-main]');
      this.thumbs = this.querySelector('[data-gallery-thumbs]');
      this.activeVideoSlide = null;
      if (!this.main) return;

      this.onSlideChange = () => this.syncToActive_();
      this.main.addEventListener('swiperslidechange', this.onSlideChange);

      this.onVariantChange = (e) => {
        if (String(e.detail?.productId) !== this.dataset.productId) return;
        this.showVariant_(e.detail.variant);
      };
      window.addEventListener('product-form:variant-change', this.onVariantChange);

      await customElements.whenDefined('swiper-container');

      this.thumbs?.initialize();
      this.main.initialize();

      [this.thumbs, this.main].forEach((el) => {
        if (!el) return;
        el.setAttribute('data-ready', '');
        el.swiper?.update();
      });

      if (this.main.swiper) this.syncToActive_();
    }

    syncToActive_() {
      const swiper = this.main.swiper;
      if (!swiper) return;

      const index = swiper.activeIndex;
      this.querySelectorAll('[data-gallery-thumb]').forEach((el, i) => el.classList.toggle('active', i === index));

      const slide = swiper.slides[index];
      if (slide?.dataset.galleryVideoId) {
        this.showVideo_(slide);
      } else {
        this.clearVideo_();
      }
    }

    // Variants with their own media set (custom.product_media) get that set; others share the default set
    showVariant_(variant) {
      if (!variant) return;
      const set =
        this.querySelector(`template[data-gallery-set="${variant.id}"]`) ||
        this.querySelector('template[data-gallery-set="default"]');

      if (set && set.dataset.gallerySet !== this.dataset.currentSet) this.applySet_(set);
      if (this.dataset.currentSet === 'default') this.swapVariantImage_(variant.featured_image);
    }

    applySet_(set) {
      const mainSwiper = this.main.swiper;
      const thumbsSwiper = this.thumbs?.swiper;
      if (!mainSwiper) return;

      this.clearVideo_();

      const replaceSlides = (container, source) => {
        if (!container || !source) return;
        container.querySelectorAll(':scope > swiper-slide').forEach((slide) => slide.remove());
        container.append(...source.cloneNode(true).children);
      };
      replaceSlides(this.main, set.content.querySelector('[data-set-main]'));
      replaceSlides(this.thumbs, set.content.querySelector('[data-set-thumbs]'));

      this.dataset.currentSet = set.dataset.gallerySet;
      this.dataset.variantSlotIndex = set.dataset.variantSlotIndex;

      if (thumbsSwiper) {
        const perView = Number(set.dataset.count) > 5 ? 5.25 : 5;
        this.thumbs.style.setProperty('--thumbs-per-view', perView);
        thumbsSwiper.params.slidesPerView = perView;
        thumbsSwiper.update();
      }
      mainSwiper.update();
      mainSwiper.slideTo(Number(set.dataset.initialIndex) || 0, 0);
      this.syncToActive_();
    }

    swapVariantImage_(image) {
      const index = Number(this.dataset.variantSlotIndex);
      if (!image?.src || !(index >= 0)) return;

      const sized = (width) => `${image.src}${image.src.includes('?') ? '&' : '?'}width=${width}`;
      const fileName = (src) => src.split('?')[0].split('/').pop();

      const mainImg = this.main.querySelectorAll(':scope > swiper-slide')[index]?.querySelector('img');
      const thumbImg = this.querySelectorAll('[data-gallery-thumb]')[index]?.querySelector('img');

      if (mainImg && fileName(mainImg.currentSrc || mainImg.src) !== fileName(image.src)) {
        mainImg.srcset = [400, 600, 800, 1200].map((w) => `${sized(w)} ${w}w`).join(', ');
        mainImg.src = sized(1200);
        mainImg.alt = image.alt || '';
      }
      if (thumbImg) {
        thumbImg.removeAttribute('srcset');
        thumbImg.src = sized(200);
      }

      this.main.swiper?.slideTo(index);
    }

    showVideo_(slide) {
      if (this.activeVideoSlide === slide) return;

      const stage = slide.querySelector('[data-gallery-video-stage]');
      const template = slide.querySelector(`[data-gallery-video="${slide.dataset.galleryVideoId}"]`);
      if (!stage || !template) return;

      this.clearVideo_();
      stage.appendChild(template.content.cloneNode(true));
      this.activeVideoSlide = slide;
      slide.querySelector('[data-gallery-poster]')?.setAttribute('aria-hidden', 'true');
    }

    clearVideo_() {
      const slide = this.activeVideoSlide;
      if (!slide) return;

      const stage = slide.querySelector('[data-gallery-video-stage]');
      if (stage) {
        stage.querySelectorAll('video').forEach((video) => {
          video.pause();
          video.removeAttribute('src');
          video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
          video.load();
        });
        stage.querySelectorAll('iframe').forEach((frame) => {
          frame.src = 'about:blank';
        });
        stage.replaceChildren();
      }

      slide.querySelector('[data-gallery-poster]')?.removeAttribute('aria-hidden');
      this.activeVideoSlide = null;
    }

    disconnectedCallback() {
      this.clearVideo_();
      this.main?.removeEventListener('swiperslidechange', this.onSlideChange);
      window.removeEventListener('product-form:variant-change', this.onVariantChange);
    }
  }

  customElements.define('product-gallery', ProductGallery);
}
